/** Resposta rápida (mensagem pronta do compositor). */
export interface QuickReply {
  id: string;
  shortcut: string;
  title: string;
  body: string;
  /** Equipe que vê a resposta; null = todas. */
  teamId: string | null;
  active: boolean;
}

/** Variáveis aceitas no texto, com a explicação para o cadastro. */
export const QUICK_REPLY_VARIABLES: Array<{ name: string; help: string }> = [
  { name: 'saudacao', help: 'Bom dia, Boa tarde ou Boa noite (pelo horário de Cuiabá)' },
  { name: 'cliente', help: 'Nome do contato' },
  { name: 'primeiro_nome', help: 'Primeiro nome do contato' },
  { name: 'operador', help: 'Primeiro nome de quem envia' },
  { name: 'chamado', help: 'Código do chamado (ex.: FLEX-908); vazio sem chamado' },
];

export interface QuickReplyContext {
  contactName: string | null;
  operatorName: string | null;
  ticketCode: string | null;
  now?: Date;
}

const firstName = (name: string | null) => {
  const first = (name ?? '').trim().split(/\s+/)[0] ?? '';
  return first ? first.charAt(0).toUpperCase() + first.slice(1).toLowerCase() : '';
};

/** Bom dia / Boa tarde / Boa noite no fuso da empresa. */
export function greeting(now: Date = new Date()): string {
  const hour = Number(now.toLocaleString('en-US', { timeZone: 'America/Cuiaba', hour: '2-digit', hour12: false })) % 24;
  return hour < 12 ? 'Bom dia' : hour < 18 ? 'Boa tarde' : 'Boa noite';
}

const plain = (s: string) =>
  s
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase();

/**
 * "/" que abre a lista: no início do texto ou depois de um espaço, até o cursor, sem espaço no meio.
 * Retorna onde começa a barra e o que foi digitado depois dela.
 */
export function slashQuery(text: string, cursor: number): { start: number; query: string } | null {
  const m = /(^|\s)\/([^\s/]*)$/.exec(text.slice(0, cursor));
  if (!m) return null;
  return { start: cursor - m[2].length - 1, query: m[2] };
}

/**
 * Respostas da equipe do atendimento (e as gerais) que combinam com o digitado: primeiro as de
 * atalho que começa igual, depois as que têm o termo no atalho ou no nome. Sem acento e maiúscula.
 */
export function matchQuickReplies(list: QuickReply[], query: string, teamId: string | null): QuickReply[] {
  const q = plain(query.trim());
  const visible = list.filter((r) => r.active && (r.teamId === null || r.teamId === teamId));
  if (!q) return visible;
  const starts = visible.filter((r) => r.shortcut.startsWith(q));
  const contains = visible.filter((r) => !starts.includes(r) && (r.shortcut.includes(q) || plain(r.title).includes(q)));
  return [...starts, ...contains];
}

/**
 * Preenche as variáveis. Valor que não existe (ex.: sem chamado) vira vazio, e os espaços e a
 * pontuação que sobram em volta são arrumados ("Olá , tudo bem" → "Olá, tudo bem").
 */
export function fillQuickReply(body: string, ctx: QuickReplyContext): string {
  const values: Record<string, string> = {
    saudacao: greeting(ctx.now),
    cliente: (ctx.contactName ?? '').trim(),
    primeiro_nome: firstName(ctx.contactName),
    operador: firstName(ctx.operatorName),
    chamado: (ctx.ticketCode ?? '').trim(),
  };
  return body
    .replace(/\{([^{}\s]+)\}/g, (all, name: string) => (name.toLowerCase() in values ? values[name.toLowerCase()] : all))
    .replace(/[ \t]+([,.!?;:])/g, '$1')
    .replace(/[ \t]{2,}/g, ' ')
    .replace(/^[ \t]+|[ \t]+$/gm, '');
}
